import { bffUpload, legacy } from '@/shared/api/client';
import type {
  AudienceFilterPayload,
  AutomatedCampaignPayload,
  Campaign,
  CampaignListMode,
  CampaignMode,
  MetabaseCampaign,
} from '@/shared/types/campaign';
import {
  mapLegacyCampaign,
  mapLegacyMetabaseCampaign,
  mapToLegacyCampaignBody,
  type LegacyCampaign,
  type LegacyMetabaseCampaign,
} from '../model/campaign-mapper';

export async function fetchCampaigns(
  idPassbook: string,
  mode: CampaignListMode,
  options?: { userId?: string },
): Promise<Campaign[] | MetabaseCampaign[]>;
export async function fetchCampaigns(idPassbook: string, mode: 'whatsapp'): Promise<Campaign[]>;
export async function fetchCampaigns(
  idPassbook: string,
  mode: CampaignListMode | 'whatsapp',
  options?: { userId?: string },
): Promise<Campaign[] | MetabaseCampaign[]> {
  const endpoint =
    mode === 'manual'
      ? options?.userId
        ? `/messages/${idPassbook}/${options.userId}`
        : `/messages/${idPassbook}`
      : mode === 'automated'
        ? `/campaigns/${idPassbook}`
        : mode === 'upload'
          ? `/metabase-campaigns/${idPassbook}`
          : `/messages-wp/logs/${idPassbook}`;
  const res = await legacy.get<LegacyCampaign[] | Campaign[] | LegacyMetabaseCampaign[]>(
    endpoint,
    passbookHeaders(idPassbook),
  );
  if (!Array.isArray(res)) return [];
  if (mode === 'automated') return (res as LegacyCampaign[]).map(mapLegacyCampaign);
  if (mode === 'upload') return (res as LegacyMetabaseCampaign[]).map(mapLegacyMetabaseCampaign);
  return res as Campaign[];
}

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

// Legado: dash/routes/message/save.js — este cliente só "salva" a mensagem,
// sem disparo real (API POST /message-save em vez de /message).
const MESSAGE_SAVE_ONLY_PASSBOOK = '65b14fa10142472d1519d0b7';

/**
 * Todas as campanhas automatizadas, de todas as lojas, numa chamada só
 * (GET /campaigns_agenda). Alimenta a tela de agenda do fixe-admin --
 * substitui a varredura antiga de 1 request por loja.
 */
export async function fetchAllCampaigns(): Promise<LegacyCampaign[]> {
  const res = await legacy.get<LegacyCampaign[]>('/campaigns_agenda');
  return Array.isArray(res) ? res : [];
}

export async function getAutomatedCampaign(
  idPassbook: string,
  campaignId: string,
): Promise<LegacyCampaign> {
  const res = await legacy.get<{ campaign: LegacyCampaign }>(
    `/campaign/${campaignId}`,
    passbookHeaders(idPassbook),
  );
  return res.campaign;
}

export async function removeCampaign(idPassbook: string, id: string) {
  await legacy.put(`/message/remove/${id}`, undefined, passbookHeaders(idPassbook));
}

export async function saveManualCampaign(
  idPassbook: string,
  payload: { name?: string; description: string; id_department?: string; office?: string },
  options?: { multiStore?: boolean; userId?: string },
) {
  if (options?.multiStore && options.userId) {
    await legacy.post(
      '/multi-message',
      {
        id_passbook: idPassbook,
        id_user: options.userId,
        description: payload.description,
        id_department: payload.id_department ?? null,
        id_office: payload.office ?? null,
      },
      passbookHeaders(idPassbook),
    );
    return;
  }

  const endpoint = idPassbook === MESSAGE_SAVE_ONLY_PASSBOOK ? '/message-save' : '/message';

  await legacy.post(
    endpoint,
    {
      id_passbook: idPassbook,
      name: payload.name,
      description: payload.description,
      id_department: payload.id_department ?? null,
      id_office: payload.office ?? null,
    },
    passbookHeaders(idPassbook),
  );
}

export async function saveWhatsappCampaign(
  idPassbook: string,
  payload: { name: string; description: string; mais_informacao?: string },
) {
  // Legado: dash /message-wp/save → API POST /message-wp.
  await legacy.post(
    '/message-wp',
    {
      id_passbook: idPassbook,
      name: payload.name,
      description: payload.description,
      mais_informacao: payload.mais_informacao,
    },
    passbookHeaders(idPassbook),
  );
}

export async function saveAutomatedCampaign(idPassbook: string, payload: AutomatedCampaignPayload) {
  const body = mapToLegacyCampaignBody(idPassbook, payload);
  await legacy.post('/campaign', body, passbookHeaders(idPassbook));
}

/** Só liga ou desliga. Não altera horário, público nem mensagem. */
export async function setAutomatedCampaignActive(idPassbook: string, id: string, ativo: boolean) {
  await legacy.post(
    '/campaign',
    { action: 'pause', idcampanha: id, id_passbook: idPassbook, ativo },
    passbookHeaders(idPassbook),
  );
}

export async function removeAutomatedCampaign(idPassbook: string, id: string) {
  await legacy.post(
    '/campaign',
    { action: 'delete', idcampanha: id, id_passbook: idPassbook },
    passbookHeaders(idPassbook),
  );
}

/** Aniversário do sistema: a API ignora qualquer outro campo. */
export async function saveBirthdayCampaign(
  idPassbook: string,
  id: string,
  payload: { mensagem: string; frequencia_horas: string },
) {
  await legacy.post(
    '/campaign',
    {
      action: 'edit',
      idcampanha: id,
      id_passbook: idPassbook,
      mensagem: payload.mensagem,
      frequencia_horas: payload.frequencia_horas,
    },
    passbookHeaders(idPassbook),
  );
}

export async function fetchAudienceFilter(
  idPassbook: string,
  payload: Omit<AudienceFilterPayload, 'id_passbook'>,
  options?: { owner?: boolean },
): Promise<number> {
  const path = options?.owner ? '/user/owner/filter' : '/user/filter';
  const res = await legacy.post<{ result?: number }>(
    path,
    { id_passbook: idPassbook, ...payload },
    passbookHeaders(idPassbook),
  );
  return res.result ?? 0;
}

/** Base total da preview — preferir signed_up_user (fidelizados); fallback na listagem. */
export async function fetchBaseUserCount(
  idPassbook: string,
  options?: { owner?: boolean },
): Promise<number> {
  if (options?.owner) {
    try {
      const res = await legacy.get<number>(
        `/user/listByOwner/${idPassbook}`,
        passbookHeaders(idPassbook),
      );
      const n = typeof res === 'number' ? res : Number(res);
      if (Number.isFinite(n) && n >= 0) return n;
    } catch {
      /* cai no filtro abaixo */
    }
    return fetchAudienceFilter(
      idPassbook,
      { plataforma: 'all', store: 'all', tipo_disparo: 'push' },
      { owner: true },
    );
  }
  const headers = passbookHeaders(idPassbook);
  try {
    const signed = await legacy.get<{ serialNumbers?: number }>(
      `/signed_up_user/${idPassbook}`,
      headers,
    );
    const n = Number(signed?.serialNumbers);
    if (Number.isFinite(n) && n >= 0) return n;
  } catch {
    /* fallback abaixo */
  }
  try {
    const res = await legacy.get<unknown[]>(`/user/listByIdPassbook/${idPassbook}`, headers);
    return Array.isArray(res) ? res.length : 0;
  } catch {
    return 0;
  }
}

export interface StoreOption {
  _id?: string;
  idPassbook?: string;
  name?: string;
  storeCod?: string | number;
  storeName?: string;
  /** Estado. Vira o agrupamento da lista de lojas na campanha de fidelidade. */
  storeUF?: string;
  storeCity?: string;
  storeLocal?: string;
  storeType?: string;
}

export interface ClusterOption {
  nome: string;
  values: string[];
}

export async function fetchCampaignStores(idPassbook: string): Promise<StoreOption[]> {
  const res = await legacy.get<StoreOption[]>(`/stores/${idPassbook}`, passbookHeaders(idPassbook));
  return Array.isArray(res) ? res : [];
}

/** Lojas do franqueador — GET /campaign/stores/:id_user (legado owner insert). */
export async function fetchOwnerCampaignStores(userId: string): Promise<StoreOption[]> {
  const res = await legacy.get<{ stores?: StoreOption[] } | StoreOption[]>(
    `/campaign/stores/${userId}`,
    passbookHeaders(userId),
  );
  if (Array.isArray(res)) return res;
  return Array.isArray(res?.stores) ? res.stores : [];
}

export async function fetchCampaignClusters(idPassbook: string): Promise<ClusterOption[]> {
  const res = await legacy.get<ClusterOption[]>(`/clusters/${idPassbook}`, passbookHeaders(idPassbook));
  return Array.isArray(res) ? res : [];
}

export interface StateOption {
  acronym: string;
  name?: string;
}

export async function fetchStates(): Promise<StateOption[]> {
  const res = await legacy.get<StateOption[]>('/state/carregar-estados/');
  return Array.isArray(res) ? res : [];
}

export async function saveUploadCampaign(
  idPassbook: string,
  formData: FormData,
) {
  formData.set('id_passbook', idPassbook);
  await bffUpload('/bff/uploads/metabase-campaign', formData);
}

export async function uploadCampaignEmailImage(file: File): Promise<{ id: string; url: string }> {
  const fd = new FormData();
  fd.append('image', file);
  const res = await bffUpload<{ error: number; payload: { id: string; url: string } }>(
    '/bff/uploads/campaign/image',
    fd,
  );
  if (res.error !== 0) throw new Error('Falha no upload');
  return res.payload;
}

/** @deprecated use fetchCampaigns with mode */
export type { CampaignMode };
